import { createContext, useContext, useState, useEffect, useCallback } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  setTheme: () => {},
})

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Check localStorage first for instant load (no flash)
    const stored = localStorage.getItem('factory_theme')
    if (stored === 'dark' || stored === 'light') return stored
    return 'light'
  })

  // Apply the dark class to <html> whenever theme changes
  useEffect(() => {
    const root = document.documentElement
    if (theme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
    localStorage.setItem('factory_theme', theme)
  }, [theme])

  // Theme is a per-device preference: it lives only in localStorage (persisted by
  // the effect above). It is deliberately NOT synced to the server — the settings
  // store is global/system-wide, so persisting theme there made every admin share
  // one theme and let a personal preference mutate system config.
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme)
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  return useContext(ThemeContext)
}
